import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useUserPreference } from "./user-preference-context";
import { getSessionData } from "../../utils/interceptors/localStorageService";

export const DASHBOARD_LAYOUTS = {
  DEFAULT: "default",
  MODERN_GRADIENT: "modern-gradient",
  GLASSMORPHISM: "glassmorphism",
  MINIMAL: "minimal",
};

export const SELECTABLE_DASHBOARD_LAYOUTS = [
  DASHBOARD_LAYOUTS.MODERN_GRADIENT,
  DASHBOARD_LAYOUTS.MINIMAL,
];

const PREFERENCE_KEY = "dashboardLayout";
const LOCAL_STORAGE_KEY = "bl_dashboard_layout";
const BALANCE_PREF_KEY = "balanceVisible";
const BALANCE_LS_KEY = "bl_balance_visible";

const DashboardLayoutContext = createContext();

export const isSelectableDashboardLayout = (layout) =>
  SELECTABLE_DASHBOARD_LAYOUTS.includes(layout);

export const resolveDashboardLayout = (layout) =>
  isSelectableDashboardLayout(layout) ? layout : DASHBOARD_LAYOUTS.MINIMAL;

export const DashboardLayoutProvider = ({ children }) => {
  const [layout, setLayoutState] = useState(() => {
    return resolveDashboardLayout(localStorage.getItem(LOCAL_STORAGE_KEY));
  });

  // true immediately if localStorage has a cached value (returning user — no flash)
  // false if cache is empty (fresh login) — wait for server fetch before rendering
  const [prefsLoaded, setPrefsLoaded] = useState(
    () => isSelectableDashboardLayout(localStorage.getItem(LOCAL_STORAGE_KEY))
  );

  const [balanceVisible, setBalanceVisible] = useState(() => {
    const stored = localStorage.getItem(BALANCE_LS_KEY);
    return stored !== null ? stored === "true" : true;
  });

  const { updateUserPreference, getUserPreferences } = useUserPreference();

  const applyPreferences = useCallback((prefs = {}) => {
    const resolvedLayout = resolveDashboardLayout(prefs[PREFERENCE_KEY]);
    setLayoutState(resolvedLayout);
    localStorage.setItem(LOCAL_STORAGE_KEY, resolvedLayout);

    const serverBalance = prefs[BALANCE_PREF_KEY];
    if (serverBalance !== undefined && serverBalance !== null) {
      const visible = serverBalance === true || serverBalance === "true";
      setBalanceVisible(visible);
      localStorage.setItem(BALANCE_LS_KEY, String(visible));
    }
  }, []);

  // On mount: if the user is already authenticated, pull their stored preferences
  // from the server so the UI is correct even on a fresh device or after clearing cache.
  useEffect(() => {
    if (!getSessionData()) {
      setPrefsLoaded(true);
      return;
    }
    getUserPreferences([PREFERENCE_KEY, BALANCE_PREF_KEY])
      .then((prefs) => applyPreferences(prefs))
      .finally(() => setPrefsLoaded(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeLayout = useCallback(
    (newLayout) => {
      if (!isSelectableDashboardLayout(newLayout)) return;
      setLayoutState(newLayout);
      localStorage.setItem(LOCAL_STORAGE_KEY, newLayout);
      updateUserPreference(PREFERENCE_KEY, newLayout);
    },
    [updateUserPreference],
  );

  const reloadPreferences = useCallback(() => {
    if (!getSessionData()) {
      setPrefsLoaded(true);
      return;
    }
    getUserPreferences([PREFERENCE_KEY, BALANCE_PREF_KEY]).then((prefs) => {
      applyPreferences(prefs);
      setPrefsLoaded(true);
    }).catch(() => setPrefsLoaded(true));
  }, [applyPreferences, getUserPreferences]);

  const toggleBalanceVisible = useCallback(() => {
    setBalanceVisible((v) => {
      const next = !v;
      localStorage.setItem(BALANCE_LS_KEY, String(next));
      updateUserPreference(BALANCE_PREF_KEY, next);
      return next;
    });
  }, [updateUserPreference]);

  return (
    <DashboardLayoutContext.Provider value={{ layout, changeLayout, DASHBOARD_LAYOUTS, balanceVisible, toggleBalanceVisible, reloadPreferences, prefsLoaded }}>
      {children}
    </DashboardLayoutContext.Provider>
  );
};

export const useDashboardLayout = () => useContext(DashboardLayoutContext);
